import { routes } from "@/lib/routes";
import type { SiteNode, TrailStep } from "@/lib/site-structure/types";

/**
 * The site tree: every public page, where it sits and how its breadcrumb
 * reads. Breadcrumbs, their BreadcrumbList JSON-LD, the hub card grids and
 * the link-up card on articles are all derived from this list, so a page's
 * place in the structure is decided once.
 *
 * Source: the consultant's sheet (tabs „Struktur“, „Contentstruktur_neu“,
 * „Contentstruktur_alt“, final 2026-10-21) with the decisions D43–D59 in the
 * navigation decision log. `site-tree.test.ts` checks that every sitemap URL
 * has exactly one node here, that every reference resolves, and writes the
 * resulting breadcrumbs to a snapshot file for sign-off.
 *
 * The header and footer menus stay hand-written; `links.test.ts` checks them
 * against the hub grids derived from this tree.
 */

const c = routes.content;

const WISSEN: TrailStep = { path: c.wissen };
const KOMPENSATION: TrailStep = { path: c.kompensationsmassnahmen, crumb: "Kompensation" };
const KOMPENSATIONSMASSNAHMEN: TrailStep = {
  path: c.kompensationsmassnahmen,
  crumb: "Kompensationsmaßnahmen",
};
const ERNEUERBARE: TrailStep = { path: c.erneuerbareEnergien };
const WINDENERGIE: TrailStep = { path: c.kompensationWindenergie, crumb: "Windenergie" };
const PHOTOVOLTAIK: TrailStep = { path: c.kompensationPhotovoltaik, crumb: "Photovoltaik" };
const BATTERIESPEICHER: TrailStep = {
  path: c.kompensationBatteriespeicher,
  crumb: "Batteriespeicher",
};

/** Wissen articles under "Wissen › Kompensation". They have no level-2 ancestor. */
const kompensationsWissen = (path: string, crumb: string): SiteNode => ({
  path,
  crumb,
  level: "legacy",
  trail: [WISSEN, KOMPENSATION],
  upTo: c.kompensationAnfragen,
});

const windWissen = (path: string, crumb: string): SiteNode => ({
  path,
  crumb,
  level: "legacy",
  trail: [WISSEN, ERNEUERBARE, WINDENERGIE],
});

const pvWissen = (path: string, crumb: string): SiteNode => ({
  path,
  crumb,
  level: "legacy",
  trail: [WISSEN, ERNEUERBARE, PHOTOVOLTAIK],
});

const BUNDESLAENDER: readonly (readonly [slug: string, label: string])[] = [
  ["baden-wuerttemberg", "Baden-Württemberg"],
  ["bayern", "Bayern"],
  ["berlin", "Berlin"],
  ["brandenburg", "Brandenburg"],
  ["bremen", "Bremen"],
  ["hamburg", "Hamburg"],
  ["hessen", "Hessen"],
  ["mecklenburg-vorpommern", "Mecklenburg-Vorpommern"],
  ["niedersachsen", "Niedersachsen"],
  ["nordrhein-westfalen", "Nordrhein-Westfalen"],
  ["rheinland-pfalz", "Rheinland-Pfalz"],
  ["sachsen", "Sachsen"],
  ["sachsen-anhalt", "Sachsen-Anhalt"],
  ["schleswig-holstein", "Schleswig-Holstein"],
  ["thueringen", "Thüringen"],
];

export const SITE_TREE: readonly SiteNode[] = [
  // ── Outside the structure ────────────────────────────────────────────────
  { path: routes.home, crumb: "Startseite", level: "utility" },
  { path: routes.registrieren, crumb: "Registrieren", level: "utility" },
  { path: c.kontakt, crumb: "Kontakt", level: "utility" },
  { path: c.ueberUns, crumb: "Über uns", level: "utility" },
  { path: c.referenzen, crumb: "Referenzen", level: "utility" },
  { path: c.impressum, crumb: "Impressum", level: "utility" },
  { path: c.datenschutz, crumb: "Datenschutz", level: "utility" },
  { path: c.agb, crumb: "AGB", level: "utility" },
  { path: c.agbProvision, crumb: "AGB Provision", level: "utility" },
  { path: c.widerrufsbelehrung, crumb: "Widerrufsbelehrung", level: "utility" },

  // ── Level 1: hubs ────────────────────────────────────────────────────────
  { path: c.kompensationsmassnahmen, crumb: "Maßnahmen", level: 1 },
  { path: c.leistungen, crumb: "Leistungen", level: 1 },
  {
    path: c.vorhabentraeger,
    crumb: "Für Vorhabenträger",
    level: 1,
    // The money page first; ÖBB is cross-listed from Leistungen and comes last.
    cardOrder: [
      c.oekopunkteKaufen,
      c.kompensationAnfragen,
      c.kompensationsberatung,
      c.erneuerbareEnergien,
      c.oekologischeBaubegleitung,
    ],
  },
  { path: c.flaecheneigentuemer, crumb: "Für Flächeneigentümer", level: 1 },
  { path: c.wissen, crumb: "Wissen", level: 1 },

  // ── Maßnahmen ────────────────────────────────────────────────────────────
  // Ökopunkte has its own path: its breadcrumb starts at the root (sheet
  // „Sonderstellung Ökopunkte“), while the Maßnahmen hub still lists it.
  {
    path: c.oekopunkte,
    crumb: "Ökopunkte",
    level: 2,
    hubs: [c.kompensationsmassnahmen],
    card: {
      icon: "leaf",
      links: [
        { path: c.wasSindOekopunkte, label: "Was sind Ökopunkte?" },
        { path: c.preisindex, label: "Aktuelle Preise je Bundesland" },
      ],
    },
    cardFact: "offerCount",
  },
  {
    path: c.ausgleichsflaechen,
    crumb: "Ausgleichsflächen",
    level: 2,
    parent: c.kompensationsmassnahmen,
    card: { icon: "map" },
    cardFact: "listedHectares",
  },
  { path: c.biotopausgleich, crumb: "Biotopausgleich", level: 3, parent: c.ausgleichsflaechen },
  {
    path: c.ackerlandStilllegung,
    crumb: "Ackerland & Stilllegung",
    level: 3,
    parent: c.ausgleichsflaechen,
  },
  {
    path: c.forstausgleichWaldumwandlung,
    crumb: "Forstausgleich & Waldumwandlung",
    level: 3,
    parent: c.ausgleichsflaechen,
  },
  {
    path: c.landschaftsbildErholung,
    crumb: "Landschaftsbild & Erholung",
    level: 3,
    parent: c.ausgleichsflaechen,
  },
  {
    path: c.artenschutz,
    crumb: "Artenschutz",
    level: 2,
    parent: c.kompensationsmassnahmen,
    card: { icon: "shieldCheck" },
  },
  { path: c.cefMassnahmen, crumb: "CEF-Maßnahmen", level: 3, parent: c.artenschutz },
  { path: c.fcsMassnahmen, crumb: "FCS-Maßnahmen", level: 3, parent: c.artenschutz },

  // ── Leistungen ───────────────────────────────────────────────────────────
  {
    path: c.oekopunkteVermittlung,
    crumb: "Ökopunkte-Vermittlung",
    level: 2,
    parent: c.leistungen,
    card: {
      icon: "arrowLeftRight",
      links: [
        { path: routes.schnellanfrage, label: "Ökopunkte anfragen" },
        { path: routes.schnellangebot, label: "Ökopunkte anbieten" },
      ],
    },
  },
  {
    path: c.flaechenvermittlung,
    crumb: "Flächenvermittlung",
    level: 2,
    parent: c.leistungen,
    card: {
      icon: "mapPin",
      links: [
        { path: c.flaecheAnbieten, label: "Fläche anbieten" },
        { path: c.wasSindAusgleichsflaechen, label: "Was sind Ausgleichsflächen?" },
      ],
    },
  },
  {
    path: c.rechtlicheAbsicherung,
    crumb: "Rechtliche Absicherung & Verträge",
    level: 2,
    parent: c.leistungen,
    card: {
      icon: "fileText",
      links: [
        { path: c.dinglicheSicherung, label: "Dingliche Sicherung" },
        { path: c.dinglicheSicherungIn("niedersachsen"), label: "Sicherung in Niedersachsen" },
      ],
    },
  },
  {
    path: c.behoerdenabstimmung,
    crumb: "Behördenabstimmung",
    level: 2,
    parent: c.leistungen,
    card: { icon: "landmark" },
  },
  {
    path: c.oekologischeBaubegleitung,
    crumb: "Ökologische Baubegleitung",
    level: 2,
    parent: c.leistungen,
    hubs: [c.vorhabentraeger],
    card: { icon: "hardHat" },
  },
  {
    path: c.landschaftspflegerischerBegleitplan,
    crumb: "LBP-Beratung",
    level: 2,
    parent: c.leistungen,
    card: { icon: "clipboardList" },
  },

  // ── Für Vorhabenträger ───────────────────────────────────────────────────
  {
    path: c.oekopunkteKaufen,
    crumb: "Ökopunkte kaufen",
    level: 2,
    parent: c.oekopunkte,
    hubs: [c.vorhabentraeger],
    card: {
      icon: "coins",
      links: [
        { path: c.preisindex, label: "Preise je Bundesland" },
        { path: c.bundeslandUebersicht, label: "Ökopunkte in Ihrem Bundesland" },
      ],
    },
    cardFact: "offerCount",
  },
  {
    path: c.kompensationAnfragen,
    crumb: "Kompensation anfragen",
    level: 2,
    parent: c.vorhabentraeger,
    card: {
      icon: "send",
      links: [
        { path: routes.schnellanfrage, label: "Zur Schnellanfrage" },
        { path: c.naturraumPlz, label: "Naturraum per PLZ bestimmen" },
      ],
    },
  },
  {
    path: c.kompensationsberatung,
    crumb: "Kompensationsberatung",
    level: 2,
    parent: c.vorhabentraeger,
    card: {
      icon: "briefcase",
      tone: "dark",
      cta: { path: `${c.kompensationsberatung}#anfrage`, label: "Beratung anfordern" },
    },
  },
  {
    path: c.erneuerbareEnergien,
    crumb: "Erneuerbare Energien",
    level: 2,
    parent: c.vorhabentraeger,
    card: { icon: "zap" },
  },
  {
    path: c.kompensationWindenergie,
    crumb: "Windenergie-Projekte",
    level: 3,
    parent: c.erneuerbareEnergien,
  },
  {
    path: c.kompensationPhotovoltaik,
    crumb: "PV-Projekte",
    level: 3,
    parent: c.erneuerbareEnergien,
  },
  {
    path: c.kompensationBatteriespeicher,
    crumb: "Batteriespeicher-Projekte",
    level: 3,
    parent: c.erneuerbareEnergien,
  },

  // ── Für Flächeneigentümer ────────────────────────────────────────────────
  {
    path: c.oekopunkteVerkaufen,
    crumb: "Ökopunkte verkaufen",
    level: 2,
    parent: c.oekopunkte,
    hubs: [c.flaecheneigentuemer],
    card: {
      icon: "coins",
      links: [
        { path: c.wasSindOekopunkte, label: "Was sind Ökopunkte?" },
        { path: c.preisindex, label: "Aktuelle Preise je Bundesland" },
      ],
    },
  },
  {
    path: c.flaecheAnbieten,
    crumb: "Fläche anbieten",
    level: 2,
    parent: c.flaecheneigentuemer,
    card: {
      icon: "mapPin",
      links: [
        { path: routes.schnellangebot, label: "Zum Schnellangebot" },
        { path: c.ackerlandStilllegung, label: "Ausgleich auf Ackerland" },
      ],
    },
  },
  {
    path: c.flaecheBewertenLassen,
    crumb: "Fläche bewerten lassen",
    level: 2,
    parent: c.flaecheneigentuemer,
    card: {
      icon: "search",
      title: "Fläche kostenfrei bewerten lassen",
      tone: "dark",
      cta: { path: c.flaecheBewertenLassen, label: "Flächenanalyse starten" },
    },
  },
  {
    path: c.potenzialanalyseFlaeche,
    crumb: "Potenzialanalyse",
    level: 2,
    parent: c.flaecheneigentuemer,
    card: {
      icon: "fileBarChart",
      title: "Potenzialanalyse Ihrer Fläche",
      links: [{ path: c.flaecheBewertenLassen, label: "Erst die kostenfreie Kurzanalyse" }],
    },
  },

  // ── Existing pages under Ökopunkte ───────────────────────────────────────
  { path: c.preisindex, crumb: "Was kostet ein Ökopunkt?", level: "legacy", parent: c.oekopunkte },
  {
    path: c.bundeslandUebersicht,
    crumb: "Ökokonto-Verordnungen",
    level: "legacy",
    parent: c.oekopunkte,
  },
  ...BUNDESLAENDER.map(
    ([slug, label]): SiteNode => ({
      path: c.oekopunkteIn(slug),
      crumb: `Ökopunkte in ${label}`,
      level: "legacy",
      parent: c.oekopunkte,
    })
  ),
  {
    path: c.oekopunkteImSaarland,
    crumb: "Ökopunkte im Saarland",
    level: "legacy",
    parent: c.oekopunkte,
  },

  // Buyer persona pages sit under „Ökopunkte kaufen“, seller ones under „Ökopunkte verkaufen“.
  {
    path: c.projektentwickler,
    crumb: "Projektentwickler",
    level: "legacy",
    parent: c.oekopunkteKaufen,
  },
  {
    path: c.infrastrukturentwickler,
    crumb: "Infrastruktur",
    level: "legacy",
    parent: c.oekopunkteKaufen,
  },
  {
    path: c.erneuerbareEnergienEntwickler,
    crumb: "Wind- und Solarprojekte",
    level: "legacy",
    parent: c.oekopunkteKaufen,
  },
  { path: c.netzbetreiber, crumb: "Netzbetreiber", level: "legacy", parent: c.oekopunkteKaufen },
  { path: c.kommune, crumb: "Kommunen", level: "legacy", parent: c.oekopunkteKaufen },
  {
    path: c.informationenFuerGemeinden,
    crumb: "Informationen für Gemeinden",
    level: "legacy",
    parent: c.oekopunkteKaufen,
  },
  { path: c.forstwirt, crumb: "Forstwirte", level: "legacy", parent: c.oekopunkteVerkaufen },
  { path: c.stiftung, crumb: "Stiftungen", level: "legacy", parent: c.oekopunkteVerkaufen },

  // ── Existing pages under the hubs ────────────────────────────────────────
  {
    path: c.naturraumPlz,
    crumb: "Naturraum per PLZ",
    level: "legacy",
    parent: c.vorhabentraeger,
    upTo: c.kompensationAnfragen,
  },
  {
    path: c.bfnNaturraum,
    crumb: "BfN-Naturräume",
    level: "legacy",
    parent: c.flaecheneigentuemer,
    upTo: c.flaecheAnbieten,
  },
  { path: c.landVerpachten, crumb: "Land verpachten", level: "legacy", parent: c.flaecheAnbieten },
  {
    path: c.landVerpachtenSteuer,
    crumb: "Land verpachten: Steuer",
    level: "legacy",
    parent: c.flaecheAnbieten,
  },
  {
    path: c.landVerpachtenFuerSolarpark,
    crumb: "Land für Solarpark verpachten",
    level: "legacy",
    parent: c.flaecheAnbieten,
  },
  { path: c.agrarpacht, crumb: "Agrarpacht", level: "legacy", parent: c.flaecheAnbieten },
  {
    path: c.agrarpachtPreis,
    crumb: "Agrarpacht pro Hektar",
    level: "legacy",
    parent: c.flaecheAnbieten,
  },
  { path: c.waldVerpachten, crumb: "Wald verpachten", level: "legacy", parent: c.flaecheAnbieten },

  // ── Wissen ───────────────────────────────────────────────────────────────
  { path: c.glossar, crumb: "Glossar", level: "legacy", parent: c.wissen, upTo: c.oekopunkte },
  {
    path: c.alternativeEinnahmequellen,
    crumb: "Alternative Einnahmequellen",
    level: "legacy",
    trail: [WISSEN, { path: c.flaecheneigentuemer }],
    upTo: c.flaecheAnbieten,
  },
  ...[
    [c.wasSindOekopunkte, "Was sind Ökopunkte?"],
    [c.dasSystemOekopunkte, "Das System Ökopunkte"],
    [c.oekopunkteUmwelt, "Ökopunkte und Umwelt"],
    [c.oekopunkteKaufenVerkaufen, "Ökopunkte kaufen und verkaufen"],
  ].map(
    ([path, crumb]): SiteNode => ({
      path,
      crumb,
      level: "legacy",
      trail: [WISSEN, { path: c.oekopunkte }],
    })
  ),
  kompensationsWissen(c.compensationTypes, "Kompensationsarten im Überblick"),
  kompensationsWissen(c.kompensationsraumVsNaturraum, "Kompensationsraum vs. Naturraum"),
  kompensationsWissen(c.eingriffsregelungVergleich, "Eingriffsregelung im Vergleich"),
  kompensationsWissen(c.nrwKompensationsraeume, "Kompensationsräume NRW"),
  kompensationsWissen(c.berlinNaturraumBindung, "Kompensationsraum Berlin"),
  kompensationsWissen(c.brandenburgLapro, "Naturräume Brandenburg"),
  kompensationsWissen(c.flaechenverbrauch, "Flächenverbrauch"),
  {
    path: c.wasSindKompensationsmassnahmen,
    crumb: "Was sind Kompensationsmaßnahmen?",
    level: "legacy",
    trail: [WISSEN, KOMPENSATIONSMASSNAHMEN],
    upTo: c.kompensationAnfragen,
  },
  {
    path: c.wasSindAusgleichsflaechen,
    crumb: "Was sind Ausgleichsflächen?",
    level: "legacy",
    trail: [WISSEN, KOMPENSATIONSMASSNAHMEN, { path: c.ausgleichsflaechen }],
  },
  {
    path: c.ausgleichsflaechenOekopunkte,
    crumb: "Ausgleichsflächen und Ökopunkte",
    level: "legacy",
    trail: [WISSEN, KOMPENSATIONSMASSNAHMEN, { path: c.ausgleichsflaechen }],
  },
  ...[
    [c.dinglicheSicherung, "Dingliche Sicherung"],
    [c.dinglicheSicherungIn("niedersachsen"), "Dingliche Sicherung in Niedersachsen"],
    [c.pachtvertraege, "Pachtverträge"],
  ].map(
    ([path, crumb]): SiteNode => ({
      path,
      crumb,
      level: "legacy",
      trail: [WISSEN, { path: c.rechtlicheAbsicherung }],
    })
  ),
  {
    path: c.agrarpachtSteuererklaerung,
    crumb: "Agrarpacht in der Steuererklärung",
    level: "legacy",
    trail: [WISSEN, { path: c.flaecheneigentuemer }],
    upTo: c.flaecheAnbieten,
  },
  ...[
    [c.jagd, "Jagdrevier pachten"],
    [c.jagdpachtPreise, "Jagdpacht-Preise"],
    [c.jagdpachtSteuer, "Jagdpacht versteuern"],
    [c.jagdpachtBerechnung, "Jagdpacht berechnen"],
  ].map(
    ([path, crumb]): SiteNode => ({
      path,
      crumb,
      level: "legacy",
      trail: [WISSEN, { path: c.landVerpachten }],
    })
  ),
  windWissen(c.windenergieFaqFlaecheneigner, "FAQ für Flächeneigner"),
  windWissen(c.windenergieFaqAnlagenbetreiber, "FAQ für Anlagenbetreiber"),
  windWissen(c.windkraftanlagenAufDemAcker, "Windkraft auf dem Acker"),
  windWissen(c.windkraftanlagenImWald, "Windkraftanlagen im Wald"),
  windWissen(c.windenergieNaturArtenschutz, "Windenergie und Naturschutz"),
  windWissen(c.windenergieLandschaftsbild, "Windräder und Landschaftsbild"),
  windWissen(c.windenergieGenehmigung, "Windenergieanlagen genehmigen"),
  {
    path: c.batteriespeicher,
    crumb: "Batteriespeicher für Wind und PV",
    level: "legacy",
    trail: [WISSEN, ERNEUERBARE, BATTERIESPEICHER],
  },
  pvWissen(c.solarparkBauen, "Solarpark bauen"),
  pvWissen(c.agriPv, "Agri-PV"),
  pvWissen(c.wieGrossSolarpark, "Größe eines Solarparks"),
  pvWissen(c.wieGrossPv, "Größe einer PV-Freiflächenanlage"),
  pvWissen(c.photovoltaikProHektar, "Photovoltaik pro Hektar"),
  pvWissen(c.checkPvFlaechen, "Welche Flächen eignen sich für PV?"),
  pvWissen(c.pvFreiflaechenVoraussetzung, "Voraussetzungen für PV-Freiflächen"),
  pvWissen(c.pvPlatzbedarfPachtpreise, "Platzbedarf und Pachtpreise"),
  pvWissen(c.naturvertraeglichePv, "Naturverträgliche Freiflächen-PV"),
  pvWissen(c.freiflaechenRueckbau, "Rückbau von Solarparks"),
  pvWissen(c.freiflaechenVerkaufenOderVerpachten, "Freiflächen verkaufen oder verpachten?"),
  pvWissen(c.pvAufPachtflaechen, "PV auf Pachtflächen"),
  pvWissen(c.pvOderWindkraft, "Photovoltaik oder Windkraft?"),
  pvWissen(c.solarparkGemeinde, "Solarpark in der Gemeinde"),
  pvWissen(c.solarInvestment, "Solar-Investment auf schwachen Böden"),
  pvWissen(c.pvAnAutobahnen, "PV an Autobahnen und Bahnstrecken"),
  pvWissen(c.photovoltaikFaq, "Photovoltaik-FAQ"),
];
